import { Box, Button, Typography } from '@mui/material'
import { Table } from 'antd'

import { message } from 'antd'
import dayjs from 'dayjs'
import { FiPrinter } from 'react-icons/fi'
import CircularLoading from '../../../components/CircularProgress'
import Pagination from '../../../components/Pagination'
import { Tag } from '../../../components/Tag'
import TrackingProviderImage from '../../../components/TrackingProviderImage'
import { ERRORS_MESSAGE_MAPPER } from '../../../constants/errors'
import { ONLY_DATE_FORMAT, ONLY_TIME_FORMAT } from '../../../constants/global'
import PageUrls from '../../../constants/page-urls'
import { axiosInstance } from '../../../hooks/useAxios'
import { IOrder, OrderShippingProvider } from '../../../types/order'
import { ITrackingPrintedHistory } from '../../../types/trackings-printed-history'
import { IFilterMapped } from './TrackingPrintedHistoryFilter'
import { pdf } from '@react-pdf/renderer'
import PDFTrackingSummary from './PDFTrackingSummary'
import PDFTrackingOrdersSummary from './PDFTrackingOrdersSummary'
import { numberFormat } from '../../../utils'
import { useUser } from '../../../hooks/useUser'

interface ICategoriesTableProps {
  isLoading: boolean
  data: ITrackingPrintedHistory[]
  onUpdateCategorySuccess: () => void
  filter: IFilterMapped
  onFilterChange: (filter: IFilterMapped) => void
  total: number
}

enum PdfType {
  summary = 'summary',
  order = 'order',
}

const TrackingsPrintedHistoryTable = ({
  isLoading,
  data,
  filter,
  onFilterChange,
  total,
}: ICategoriesTableProps) => {
  const { user } = useUser()

  const handleOnPrint = async (
    history: ITrackingPrintedHistory,
    pdfType: PdfType,
  ) => {
    try {
      const orderIds = history.orders.map((order) => order.order)
      const response = await axiosInstance.post<IOrder[]>('/order/getByIds', {
        ids: orderIds,
      })
      await openPDF(response.data, pdfType, history)
    } catch (error: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[error.response?.data?.message ?? ''] ||
        'ไม่สามารถทำรายการได้'
      message.error(messageError)
    }
  }

  const openPDF = async (
    orders: IOrder[],
    pdfType: PdfType,
    history: ITrackingPrintedHistory,
  ) => {
    const blob = await pdf(
      pdfType === PdfType.summary ? (
        <PDFTrackingSummary
          user={user}
          history={history}
          orders={orders}
          size={'A4'}
        />
      ) : (
        <PDFTrackingOrdersSummary orders={orders} size={'A4'} />
      ),
    ).toBlob()
    const pdfURL = URL.createObjectURL(blob)
    window.open(pdfURL, '_blank')
  }

  const columns = [
    {
      title: 'ลำดับ',
      dataIndex: 'numberRow',
      key: 'numberRow',
      align: 'center' as const,
      width: 80,
      render: (numberRow: number) => {
        return (
          <Box>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              textAlign={'center'}
            >
              {numberRow}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'ขนส่ง',
      dataIndex: 'provider',
      key: 'provider',
      align: 'center' as const,
      width: 120,
      render: (provider: string) => {
        return (
          <TrackingProviderImage
            trackingProvider={provider as OrderShippingProvider}
          />
        )
      },
    },
    {
      title: 'รายการหมายเลขพัสดุ',
      dataIndex: 'orders',
      key: 'orders',
      align: 'left' as const,
      render: (orders: { order: string; tracking: string }[]) => {
        return (
          <>
            <Box display={'flex'} flexWrap={'wrap'} gap={1}>
              {orders.map((order) => {
                return (
                  <a
                    href={`${PageUrls.Orders.Detail}/${order.order}`}
                    style={{ cursor: 'pointer' }}
                    target="_blank"
                  >
                    <Tag key={order.tracking} color="primary">
                      {order.tracking}
                    </Tag>
                  </a>
                )
              })}
            </Box>
          </>
        )
      },
    },
    {
      title: 'จำนวนรายการ',
      dataIndex: 'amount',
      key: 'amount',
      width: 160,
      align: 'center' as const,
      render: (_: string, history: ITrackingPrintedHistory) => {
        return <>{numberFormat(history.orders.length)}</>
      },
    },
    {
      title: 'วันที่พิมพ์',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      align: 'center' as const,
      render: (createdAt: string, history: ITrackingPrintedHistory) => {
        return (
          <Box>
            {history.actor ?? ''}
            <Typography variant="subtitle2" color="text.secondary">
              {dayjs(createdAt).format(ONLY_DATE_FORMAT)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {dayjs(createdAt).format(ONLY_TIME_FORMAT)}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'ใบสรุปการจัดเตรียมสินค้า',
      dataIndex: '',
      key: 'action',
      width: 400,
      align: 'center' as const,
      render: (_: undefined, history: ITrackingPrintedHistory) => {
        return (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
            flexWrap={'wrap'}
            gap={1}
          >
            <Button
              variant="contained"
              size="small"
              color="success"
              startIcon={<FiPrinter />}
              onClick={() => handleOnPrint(history, PdfType.summary)}
            >
              สรุปรวมรายการ
            </Button>
            <Button
              variant="contained"
              size="small"
              color="secondary"
              startIcon={<FiPrinter />}
              onClick={() => handleOnPrint(history, PdfType.order)}
            >
              แยกรายการ
            </Button>
          </Box>
        )
      },
    },
  ]

  return (
    <>
      <CircularLoading isLoading={isLoading} />
      <Table
        size={'middle'}
        columns={columns}
        scroll={{
          x: columns.reduce(
            (pre, cur) => (cur.width ? cur.width + pre : pre),
            0,
          ),
        }}
        dataSource={data?.map(
          (category: ITrackingPrintedHistory, index: number) => {
            return {
              ...category,
              key: category.objectId,
              numberRow:
                index + 1 + ((filter.page || 0) - 1) * (filter.perPage || 0),
            }
          },
        )}
        bordered
        pagination={false}
      />
      <Pagination
        total={total}
        current={filter.page!}
        pageSize={filter.perPage!}
        onChange={(page, perPage) =>
          onFilterChange({ ...filter, page, perPage })
        }
      />
    </>
  )
}

export default TrackingsPrintedHistoryTable
